import type { Event } from "@mux/sdk/v2/client";
import { createSimpleContext } from "@mux/ui/context";
import { createGlobalEmitter } from "@/utils/event-emitter";
import { batch, createEffect, createSignal, on, onCleanup } from "solid-js";
import { observeEventStreamResponse, openGlobalEventStream, requestEventBarrier, requestEventWatch } from "@/transport/global-events";
import { createStreamRecovery } from "@/transport/stream-recovery";
import { createSdkForServer } from "@/transport/server";
import { EVENT_STREAM_SUSPENDED_MS, EVENT_STREAM_TIMEOUT_MS, eventStreamHeartbeatDelay, eventStreamPace, waitForEventStreamVisibility } from "./event-stream-visibility";
import { createEventStreamLink } from "./event-stream-link";
import { useLanguage } from "./language";
import { usePlatform } from "./platform";
import { useServer } from "./server";
import { createServerEventScheduler } from "./server-event-scheduler";
import { backgroundChats, onBackgroundChatsChange } from "./background-chats";
import { createWorkspaceClientCache } from "./workspace-client-cache";
import { createEventStreamReplay } from "./event-stream-replay";
import { createEventStreamWatch } from "./event-stream-watch";

export type QueuedServerEvent = { directory: string; payload: Event };

export function normalizeEventInterest(directories: readonly string[]) {
  return [...new Set(["global", ...directories.map((directory) => directory.trim()).filter(Boolean)])].sort();
}

import { jitterDelay } from "@/utils/network-schedule";
import { watchSystemWake } from "@/utils/system-wake";
export const eventStreamShouldYield = (visibilityState: string | undefined) =>
  visibilityState !== "hidden";

export function eventStreamReconnectDelay(
  delay: number,
  max: number,
  random = Math.random,
) {
  const bounded = Math.min(delay, max);
  return jitterDelay(bounded, Math.min(1000, bounded), random);
}

export const { use: useGlobalSDK, provider: GlobalSDKProvider } =
  createSimpleContext({
    name: "GlobalSDK",
    init: () => {
      const language = useLanguage();
      const server = useServer();
      const platform = usePlatform();
      const abort = new AbortController();
      const streamRecovery = createStreamRecovery({ current: () => server.current?.http });
      const link = createEventStreamLink();

      const eventFetch = (() => {
        if (!platform.fetch || !server.current) return;
        try {
          const url = new URL(server.current.http.url);
          const loopback =
            url.hostname === "localhost" ||
            url.hostname === "127.0.0.1" ||
            url.hostname === "::1";
          if (url.protocol === "http:" && !loopback) return platform.fetch;
        } catch {
          return;
        }
      })();

      const currentServer = server.current;

      let onStreamEstablished: Parameters<typeof observeEventStreamResponse>[0] | undefined;
      const eventSdk = createSdkForServer({
        signal: abort.signal,
        fetch: eventFetch,
        server: currentServer?.http,
        resolveServer: () => server.current?.http,
        onResponse: observeEventStreamResponse(transport => onStreamEstablished?.(transport)),
      });
      const emitter = createGlobalEmitter<{
        [key: string]: Event;
      }>();

      let mapDirectory = (dir: string) => dir;

      const STREAM_YIELD_MS = 8;
      const RECONNECT_BASE_DELAY_MS = 500;
      const RECONNECT_MAX_DELAY_MS = 15_000;
      // On a slow network a stream spends seconds on DNS, TLS and access
      // renewal before it opens, so the stall timeout applies only once it is
      // open. Until then an attempt gets this long, doubled after each attempt
      // that ran out, up to the cap.
      const STREAM_OPEN_TIMEOUT_MS = 15_000;
      const STREAM_OPEN_TIMEOUT_MAX_MS = 60_000;
      let streamOpen = false;
      let openTimeouts = 0;
      const pageVisibility = () =>
        typeof document === "undefined" ? undefined : document.visibilityState;
      const visibility = {
        state: pageVisibility,
        listen(listener: () => void) {
          if (typeof document === "undefined") return () => undefined;
          document.addEventListener("visibilitychange", listener);
          return () => document.removeEventListener("visibilitychange", listener);
        },
      };
      const scheduler = createServerEventScheduler({
        visibility: pageVisibility,
        background: backgroundChats,
        emit(event) {
          batch(() => emitter.emit(event.directory, event.payload));
        },
      });
      const stopBackgroundChats = onBackgroundChatsChange(scheduler.backgroundChanged);

      const replay = createEventStreamReplay({
        emit: (event) => emitter.emit(event.directory, event.payload),
        enqueue: scheduler.enqueue,
        drain: scheduler.drainAll,
      });

      let streamErrorLogged = false;
      const wait = (ms: number, signal?: AbortSignal) =>
        new Promise<void>((resolve, reject) => {
          if (signal?.aborted) {
            reject(new DOMException("Aborted", "AbortError"));
            return;
          }
          const timer = setTimeout(() => {
            signal?.removeEventListener("abort", onAbort);
            resolve();
          }, ms);
          const onAbort = () => {
            clearTimeout(timer);
            reject(new DOMException("Aborted", "AbortError"));
          };
          signal?.addEventListener("abort", onAbort, { once: true });
        });
      const aborted = (error: unknown) => {
        return (
          error != null &&
          typeof error === "object" &&
          "name" in error &&
          error.name === "AbortError"
        );
      };

      let attempt: AbortController | undefined;
      // The route that delivers the established stream, for following route changes.
      let streamURL: string | undefined;
      // The current attempt's stall timeout, which follows its heartbeat pace.
      let heartbeatTimeoutMs: number = EVENT_STREAM_TIMEOUT_MS.live;
      let lastEventAt = Date.now();
      let heartbeat: ReturnType<typeof setTimeout> | undefined;
      const clearHeartbeat = () => {
        if (!heartbeat) return;
        clearTimeout(heartbeat);
        heartbeat = undefined;
      };
      const armHeartbeat = () => {
        clearHeartbeat();
        const remaining = eventStreamHeartbeatDelay({
          visibilityState: pageVisibility(),
          lastEventAt,
          now: Date.now(),
          timeoutMs: streamOpen ? heartbeatTimeoutMs : Math.min(STREAM_OPEN_TIMEOUT_MS * 2 ** openTimeouts, STREAM_OPEN_TIMEOUT_MAX_MS),
        });
        if (remaining === undefined) return;
        const due = Date.now() + remaining;
        heartbeat = setTimeout(() => {
          heartbeat = undefined;
          // A timer that runs late measured the device sleeping or the page
          // freezing, not the route: reconnect without counting a stall.
          const suspended = Date.now() - due >= EVENT_STREAM_SUSPENDED_MS;
          attempt?.abort(suspended
            ? new DOMException("Event stream paused with the page", "AbortError")
            : new DOMException("Event stream heartbeat expired", "TimeoutError"));
        }, remaining);
      };
      const resetHeartbeat = () => {
        lastEventAt = Date.now();
        armHeartbeat();
      };

      let run: Promise<void> | undefined;
      let started = false;
      let reconnectDelay = RECONNECT_BASE_DELAY_MS;
      let reconnectWait: AbortController | undefined;
      let interest = normalizeEventInterest([]);
      // The workspaces the stream follows, for policies that depend on them.
      const [covered, setCovered] = createSignal<readonly string[]>([]);
      let interestRevision = 0;
      let connected = false;
      // Transcript events arrive only for the chats the stream watches.
      const watch = createEventStreamWatch({
        request: (stream, change, signal) => {
          const connection = server.current?.http;
          if (!connection) return Promise.reject(new Error("Machine is not connected"));
          return requestEventWatch(connection, stream, change, signal, eventFetch);
        },
        reconnect: (signal) => {
          if (attempt?.signal === signal) reconnect();
        },
        drain: () => batch(scheduler.drainAll),
      });
      let barrier: { token: string; timer: ReturnType<typeof setTimeout> } | undefined;
      const clearBarrier = () => {
        if (barrier) clearTimeout(barrier.timer);
        barrier = undefined;
      };

      const nextReconnectDelay = () => {
        const max = RECONNECT_MAX_DELAY_MS;
        const delay = Math.min(reconnectDelay, max);
        reconnectDelay = Math.min(reconnectDelay * 2, max);
        return eventStreamReconnectDelay(delay, max);
      };

      const start = () => {
        if (started) return run;
        if (!server.current) return run;
        started = true;
        run = (async () => {
          while (!abort.signal.aborted && started) {
            try {
              await waitForEventStreamVisibility(visibility, abort.signal);
            } catch (error) {
              if (aborted(error)) return;
              throw error;
            }
            if (abort.signal.aborted || !started) return;

            const attemptRevision = interestRevision;
            const pace = eventStreamPace(interest);
            heartbeatTimeoutMs = link.stallTimeoutMs(pace);
            let recoveryAttempt = streamRecovery.begin(server.current?.http);
            let established = false;
            streamOpen = false;
            const replayAttempt = replay.begin(interest.filter((directory) => directory !== "global"));
            const connection = new AbortController();
            attempt = connection;
            onStreamEstablished = transport => {
              if (connection.signal.aborted) return;
              established = true;
              streamOpen = true;
              openTimeouts = 0;
              resetHeartbeat();
              streamURL = transport.url;
              recoveryAttempt = streamRecovery.begin(transport);
              recoveryAttempt.opened();
            };
            let receivedEventID: string | undefined;
            lastEventAt = Date.now();
            // The last event applied from this stream that a gap did not
            // precede: the watched chats hold every event through it.
            let applied = replayAttempt.cursor;
            const onAbort = () => {
              attempt?.abort();
            };
            abort.signal.addEventListener("abort", onAbort);
            try {
              const streamOptions = {
                signal: attempt.signal,
                query: {
                  directories: interest.join(","),
                  include: "summary" as const,
                  ...(pace === "idle" ? { heartbeat: "idle" as const } : {}),
                  ...(link.slow() ? { link: "slow" as const } : {}),
                },
                headers: replayAttempt.cursor
                  ? { "Last-Event-ID": replayAttempt.cursor }
                  : undefined,
                // Outer loop owns reconnect/backoff; generated client must not impose a 3s floor.
                sseMaxRetryAttempts: 0,
                sseDefaultRetryDelay: RECONNECT_BASE_DELAY_MS,
                onSseError: (error: unknown) => {
                  if (aborted(error)) return;
                  if (streamErrorLogged) return;
                  streamErrorLogged = true;
                  console.error("[global-sdk] event stream error", {
                    url: server.current?.http.url ?? "unavailable",
                    fetch: eventFetch ? "platform" : "webview",
                    error,
                  });
                },
                onSseEvent: (event: { id?: string }) => {
                  receivedEventID = event.id;
                },
              };
              const events = await openGlobalEventStream(eventSdk, streamOptions);
              let yielded = Date.now();
              resetHeartbeat();
              for await (const event of events.stream) {
                if (connection.signal.aborted) break;
                streamOpen = true;
                openTimeouts = 0;
                resetHeartbeat();
                recoveryAttempt.active();
                streamErrorLogged = false;
                const directory = mapDirectory(event.directory ?? "global");
                if ((event.payload as { type?: string }).type === "sync")
                  continue;
                const payload = event.payload as Event;
                const type = payload.type as string;
                if (type === "server.watched") {
                  const confirmation = payload.properties as { token?: string; gaps?: Record<string, string> };
                  if (confirmation.token) watch.received(confirmation.token, confirmation.gaps ?? {});
                  continue;
                }
                replayAttempt.receive({ directory, payload }, receivedEventID);
                if (type !== "server.resync-required") applied = replay.cursor() ?? applied;
                if (payload.type === "server.connected") {
                  connected = true;
                  const stream = (payload.properties as { stream?: string }).stream;
                  if (stream) watch.opened(stream, connection.signal);
                }
                if ((payload.type as string) === "server.barrier" &&
                    (payload.properties as { token?: string }).token === barrier?.token) clearBarrier();

                if (Date.now() - yielded < STREAM_YIELD_MS) continue;
                if (scheduler.pressured()) await scheduler.settled();
                yielded = Date.now();
                if (
                  typeof document === "undefined" ||
                  eventStreamShouldYield(document.visibilityState)
                )
                  await wait(0);
              }
            } catch (error) {
              if (!aborted(error) && !streamErrorLogged) {
                streamErrorLogged = true;
                console.error("[global-sdk] event stream failed", {
                  url: server.current?.http.url ?? "unavailable",
                  fetch: eventFetch ? "platform" : "webview",
                  error,
                });
              }
            } finally {
              connected = false;
              watch.closed(applied);
              streamURL = undefined;
              clearBarrier();
              replayAttempt.close();
              abort.signal.removeEventListener("abort", onAbort);
              attempt = undefined;
              onStreamEstablished = undefined;
              clearHeartbeat();
            }

            if (abort.signal.aborted || !started) return;
            if (pageVisibility() === "hidden") {
              reconnectDelay = RECONNECT_BASE_DELAY_MS;
              continue;
            }
            if (attemptRevision !== interestRevision) continue;
            if (recoveryAttempt.stable()) reconnectDelay = RECONNECT_BASE_DELAY_MS;
            const stalled = connection.signal.reason?.name === "TimeoutError";
            if (stalled && !established) openTimeouts += 1;
            // A heartbeat stall: the stream had opened, so its stall timeout was in force.
            if (stalled && streamOpen && pace === "live") link.stalled();
            if (stalled || (!connection.signal.aborted && established)) {
              await recoveryAttempt.failed(stalled ? "sse-stall" : "sse-error");
            }
            if (abort.signal.aborted || !started) return;
            if (attemptRevision !== interestRevision || pageVisibility() === "hidden") continue;
            const waiting = new AbortController();
            reconnectWait = waiting;
            const cancelWait = () => waiting.abort();
            abort.signal.addEventListener("abort", cancelWait, { once: true });
            try {
              await wait(nextReconnectDelay(), waiting.signal);
            } catch {
              if (abort.signal.aborted || !started) return;
            } finally {
              abort.signal.removeEventListener("abort", cancelWait);
              reconnectWait = undefined;
            }
          }
        })().finally(() => {
          run = undefined;
          scheduler.flush();
        });
        return run;
      };

      const reconnect = () => {
        streamRecovery.reset();
        interestRevision += 1;
        reconnectDelay = RECONNECT_BASE_DELAY_MS;
        reconnectWait?.abort();
        attempt?.abort();
      };

      // The stream follows its machine's route. Left on an abandoned route, it
      // would deliver late or not at all until its own stall timeout.
      createEffect(on(() => server.current?.http.url, (url) => {
        if (url && streamURL && url !== streamURL) reconnect();
      }, { defer: true }));

      const setInterest = (directories: readonly string[]) => {
        const next = normalizeEventInterest(directories);
        if (next.length === interest.length && next.every((directory, index) => directory === interest[index])) return;
        interest = next;
        setCovered(next.filter((directory) => directory !== "global"));
        reconnect();
      };

      const stop = () => {
        streamRecovery.reset();
        started = false;
        reconnectWait?.abort();
        clearBarrier();
        attempt?.abort();
        clearHeartbeat();
      };

      // A dropped connection can look open for as long as its stall timeout,
      // which may not count time the device slept. When the page is shown,
      // the network returns, or the device wakes, a barrier sent through the
      // stream proves it still delivers; a stream that cannot deliver it
      // reconnects and resumes from its cursor.
      const verify = () => {
        if (pageVisibility() === "hidden" || !server.current) return;
        if (!connected || !attempt || attempt.signal.aborted) {
          reconnect();
          return;
        }
        if (barrier) return;
        const connection = attempt;
        const token = crypto.randomUUID();
        replay.resume(token);
        const reconnectStale = () => {
          if (attempt === connection) reconnect();
        };
        // A barrier is an ordinary event, so it arrives at once whatever the heartbeat pace.
        barrier = { token, timer: setTimeout(reconnectStale, link.stallTimeoutMs("live")) };
        void requestEventBarrier(server.current.http, token, connection.signal, eventFetch).catch((error) => {
          if (connection.signal.aborted) return;
          console.warn("[global-sdk] resume barrier failed; reconnecting", error);
          reconnectStale();
        });
      };
      const onVisibility = () => {
        if (typeof document === "undefined") return;
        replay.suspend();
        if (document.visibilityState === "hidden") {
          reconnectWait?.abort();
          clearHeartbeat();
          scheduler.visibilityChanged();
          return;
        }
        verify();
      };
      let stopWakeWatch: (() => void) | undefined;
      if (typeof document !== "undefined") {
        document.addEventListener("visibilitychange", onVisibility);
        window.addEventListener("online", verify);
        stopWakeWatch = watchSystemWake(verify);
      }

      const workspaceClients = createWorkspaceClientCache((directory) =>
        createSdkForServer({
          server: server.current?.http,
          resolveServer: () => server.current?.http,
          fetch: platform.fetch,
          signal: abort.signal,
          directory,
          throwOnError: true,
        }),
      );
      const clientFor = (directory: string) => {
        if (!directory) return sdk;
        return workspaceClients.clientFor(directory) ?? sdk;
      };

      onCleanup(() => {
        if (typeof document !== "undefined") {
          document.removeEventListener("visibilitychange", onVisibility);
          window.removeEventListener("online", verify);
        }
        stopWakeWatch?.();
        stop();
        watch.dispose();
        streamRecovery.dispose();
        abort.abort();
        stopBackgroundChats();
        scheduler.drainAll();
        scheduler.dispose();
        workspaceClients.clear();
      });

      const sdk = createSdkForServer({
        server: server.current?.http,
        resolveServer: () => server.current?.http,
        fetch: platform.fetch,
        signal: abort.signal,
        throwOnError: true,
      });

      return {
        get url() {
          return server.current?.http.url ?? "";
        },
        get server() {
          return server.current?.http;
        },
        transportFetch: platform.fetch,
        client: sdk,
        clientFor,
        event: {
          catchingUp: replay.catchingUp,
          /** Whether the stream delivers every transcript event of the chat from now on. */
          watching: (sessionID: string) => !replay.catchingUp() && watch.watching(sessionID),
          /** The chats whose transcript events the page needs; the stream watches them. */
          setWatch: watch.set,
          /** Watched chats whose events the stream may have missed. */
          onWatchGaps: watch.onGaps,
          /** The workspaces the stream follows (reactive). */
          covered,
          cursor: replay.cursor,
          on: emitter.on.bind(emitter),
          listen: emitter.listen.bind(emitter),
          start,
          reconnect,
          setInterest,
          registerMapper(mapper: (dir: string) => string) {
            mapDirectory = mapper;
          },
        },
        createClient(
          opts: Omit<
            Parameters<typeof createSdkForServer>[0],
            "server" | "fetch"
          >,
        ) {
          const s = server.current;
          if (!s)
            throw new Error(language.t("error.globalSDK.serverNotAvailable"));
          return createSdkForServer({
            server: s.http,
            resolveServer: () => server.current?.http ?? s.http,
            fetch: platform.fetch,
            signal: abort.signal,
            ...opts,
          });
        },
      };
    },
  });
